import { api } from '../lib/api'

/**
 * Интерфейс для медиа-файла
 */
export interface MediaItem {
  url: string
  fileName?: string
  type?: 'pdf' | 'photo' | 'image'
  designCode?: number
}

/**
 * Нормализует URL медиа-файла для использования в браузере
 * 
 * @param media - Объект медиа с url и опциональным fileName
 * @param designCode - Код дизайна для формирования API URL
 * @returns Полный URL для загрузки медиа-файла
 */
export function getMediaUrl(media: MediaItem | string, designCode?: number): string {
  // Если передан просто строка (для обратной совместимости)
  if (typeof media === 'string') {
    const url = media
    // Если URL уже полный
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    }

    // Keep API media on the frontend origin. The backend sends
    // Cross-Origin-Resource-Policy: same-origin for these files.
    if (url.startsWith('/api/')) {
      return url
    }
    
    // Если нет designCode, возвращаем как есть (может быть относительный путь)
    if (!designCode) {
      return url
    }
    
    // Извлекаем fileName из URL
    const fileName = url.split('/').pop() || url
    return buildMediaApiUrl(designCode, fileName)
  }
  
  // Если media объект
  const url = media.url || ''
  
  // Если URL пустой, возвращаем пустую строку
  if (!url) {
    return ''
  }
  
  // Если URL уже полный
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url
  }

  // Backend media URLs are already browser-ready and should be served through
  // the frontend proxy instead of being rewritten to another origin.
  if (url.startsWith('/api/')) {
    return url
  }
  
  // Используем designCode из media или переданный параметр
  const code = media.designCode || designCode
  
  // Если нет designCode или он равен 0, возвращаем URL как есть (может быть полным URL от WB)
  if (!code || code === 0) {
    // Если URL начинается с /, это относительный путь - возвращаем как есть
    // (браузер попытается загрузить относительно текущего домена)
    return url
  }
  
  // Извлекаем fileName из URL или используем media.fileName
  // media.url может быть относительным путем в Yandex.Disk: "/PDF/100010.pdf" или "100010.pdf"
  // media.fileName может быть: "100010.pdf" или полный путь
  let fileName: string;
  
  if (media.fileName) {
    // Если есть fileName, используем его (может быть полный путь или только имя)
    fileName = media.fileName.includes('/') 
      ? media.fileName.split('/').pop() || media.fileName
      : media.fileName;
  } else {
    // Извлекаем из URL (последняя часть пути)
    fileName = url.split('/').pop() || url;
  }
  
  // Убираем начальный слэш если есть
  fileName = fileName.startsWith('/') ? fileName.slice(1) : fileName;
  
  return buildMediaApiUrl(code, fileName)
}

/**
 * Формирует полный URL для доступа к медиа-файлу через API
 * 
 * @param designCode - Код дизайна
 * @param fileName - Имя файла
 * @returns Полный URL
 */
function buildMediaApiUrl(designCode: number, fileName: string): string {
  // Получаем базовый URL API
  const apiBaseUrl = api.defaults.baseURL || ''
  
  // Если baseURL относительный (начинается с /), используем window.location.origin
  if (apiBaseUrl.startsWith('/')) {
    return `${window.location.origin}${apiBaseUrl}/designs/media/${designCode}/${fileName}`
  }
  
  // Если baseURL абсолютный, используем его
  if (apiBaseUrl) {
    // Убираем /api/v1 если есть, так как endpoint уже содержит /designs/media
    const base = apiBaseUrl.replace(/\/api\/v1$/, '')
    return `${base}/api/v1/designs/media/${designCode}/${fileName}`
  }
  
  // Fallback: используем window.location.origin
  return `${window.location.origin}/api/v1/designs/media/${designCode}/${fileName}`
}

/**
 * Нормализует массив медиа-файлов
 * 
 * @param mediaArray - Массив медиа-файлов
 * @param designCode - Код дизайна
 * @returns Массив медиа-файлов с нормализованными URL
 */
export function normalizeMediaUrls(
  mediaArray: MediaItem[] | undefined,
  designCode: number
): MediaItem[] {
  if (!mediaArray || !Array.isArray(mediaArray)) {
    return []
  }
  
  return mediaArray.map(media => ({
    ...media,
    url: getMediaUrl(media, designCode),
    designCode: designCode,
  }))
}
